import { useState } from "react";
import { FaUser, FaEnvelope, FaLock, FaPhoneAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import SocialSign from './SocialSign';
import PopUpVerificationCode from '../Shared/PopUpVerificationCode/PopUpVerificationCode';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Definicje wyrażeń regularnych
const NAME_REGEX =
  /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+(?:[-\s][a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+)*$/;
const EMAIL_REGEX = /\S+@\S+\.\S+/;
const PASSWORD_RULES = {
  minLength: 8,
  hasUppercase: /[A-Z]/,
  hasLowercase: /[a-z]/,
  hasDigit: /[0-9]/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/,
};
const PHONENUMBER_REGEX = /^(\+48[-\s.]?)?(\d{3}[-\s.]?\d{3}[-\s.]?\d{3})$/;

const SignUp = ({ setSignUp }) => {
  // Stany dla każdego pola
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  

  // Stany błędów
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNumber: "",
  });

  // Flaga do sprawdzania zajętości e-maila i nr telefonu
  const [isEmailChecking, setIsEmailChecking] = useState(false);
  const [isPhoneNumberChecking, setIsPhoneNumberChecking] = useState(false);

  // Walidacja pól
  const validateFields = async () => {
    let isValid = true;
    const newErrors = {};

    // Walidacja imienia
    if (!firstName.trim()) {
      newErrors.firstName = "Imię jest wymagane";
      isValid = false;
    } else if (!NAME_REGEX.test(firstName)) {
      newErrors.firstName =
        "Imię nie może zawierać cyfr ani znaków specjalnych";
      isValid = false;
    }

    // Walidacja nazwiska
    if (!lastName.trim()) {
      newErrors.lastName = "Nazwisko jest wymagane";
      isValid = false;
    } else if (!NAME_REGEX.test(lastName)) {
      newErrors.lastName =
        "Nazwisko nie może zawierać cyfr ani znaków specjalnych";
      isValid = false;
    }

    // Walidacja e-maila
    if (!email.trim()) {
      newErrors.email = "Adres email jest wymagany";
      isValid = false;
    } else if (!EMAIL_REGEX.test(email)) {
      newErrors.email = "Podaj poprawny adres email";
      isValid = false;
    } else {
      // Asynchroniczna walidacja zajętości e-maila
      setIsEmailChecking(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/checkEmail?email=${encodeURIComponent(email)}`);
        if (response.ok) {
         let exists = false;
         const contentType = response.headers.get("content-type") || "";
         if (contentType.includes("application/json")) {
          const data = await response.json();
          exists = typeof data === "boolean" ? data : !!data.exists;
         } else {
          const text = (await response.text()).trim().toLowerCase();
          exists = text === "true";
         }
         if (exists) {
            newErrors.email = "Ten adres email jest już używany. Spróbuj się zalogować.";
            isValid = false;
          }
        } else {
          console.error("Błąd przy sprawdzaniu e-maila");
        }
      } catch (error) {
        console.error("Wystąpił błąd podczas sprawdzania e-maila:", error);
      }
      setIsEmailChecking(false);
    }

    // Walidacja hasła
    if (!password.trim()) {
      newErrors.password = "Hasło jest wymagane";
      isValid = false;
    } else {
      if (password.length < PASSWORD_RULES.minLength) {
        newErrors.password =
          `Hasło musi mieć co najmniej ${PASSWORD_RULES.minLength} znaków`;
        isValid = false;
      }
      if (!PASSWORD_RULES.hasUppercase.test(password)) {
        newErrors.password =
          "Hasło musi zawierać co najmniej jedną dużą literę";
        isValid = false;
      }
      if (!PASSWORD_RULES.hasLowercase.test(password)) {
        newErrors.password =
          "Hasło musi zawierać co najmniej jedną małą literę";
        isValid = false;
      }
      if (!PASSWORD_RULES.hasDigit.test(password)) {
        newErrors.password = "Hasło musi zawierać co najmniej jedną cyfrę";
        isValid = false;
      }
      if (!PASSWORD_RULES.hasSpecialChar.test(password)) {
        newErrors.password =
          "Hasło musi zawierać co najmniej jeden znak specjalny";
        isValid = false;
      }
    }

    // Walidacja nr telefonu
    if (phoneNumber.trim() && !PHONENUMBER_REGEX.test(phoneNumber)) {
      if (!PHONENUMBER_REGEX.test(phoneNumber)) {
        newErrors.phoneNumber = "Numer telefonu nie ma poprawnego formatu";
        isValid = false;
      } else {
        setIsPhoneNumberChecking(true);
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/user/checkPhoneNumber?phoneNumber=${encodeURIComponent(phoneNumber)}`,
          );
          if (response.ok) {
            const users = await response.json();
            const phoneNumberDoesNotExist = users.some(
              (user) => user.phoneNumber === phoneNumber,
            );
            if (phoneNumberDoesNotExist) {
              newErrors.phoneNumber =
                "Konto z podanym numerem telefonu już istnieje.";
              isValid = false;
            }
          } else {
            console.error("Błąd przy sprawdzaniu numeru telefonu");
          }
        } catch (error) {
          console.error(
            "Wystąpił błąd podczas sprawdzania numeru telefonu:",
            error,
          );
        }
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  // Obsługa wysłania formularza
  const handleSubmit = async (e) => {
    e.preventDefault(); // Zapobiega przeładowaniu strony
    if (!(await validateFields())) {
      return;
    }

    const userData = {
      firstName,
      lastName,
      email,
      password,
      phoneNumber: phoneNumber.trim() === "" ? null : phoneNumber,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/registration/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(userData),
        },
      );

      if (response.ok) {
        // setSignUp(false);
        setRegisteredEmail(userData.email);
        setShowVerificationModal(true);
      } else {
        console.error("Błąd przy tworzeniu użytkownika", response);
      }
    } catch (error) {
      console.error("Wystąpił błąd:", error);
    }
  };

  return (
    <>
      <form className="sign-up-form" onSubmit={handleSubmit}>
        <h2 className="title">Zarejestruj się</h2>

        <div className={`input-field ${errors.firstName ? "has-error" : ""}`}>
          <span className="fIcon">
            <FaUser />
          </span>
          <input
            placeholder="Imię"
            name="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {errors.firstName && (
            <small className="error-text">{errors.firstName}</small>
          )}
        </div>

        <div className={`input-field ${errors.lastName ? "has-error" : ""}`}>
          <span className="fIcon">
            <FaUser />
          </span>
          <input
            placeholder="Nazwisko"
            name="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {errors.lastName && (
            <small className="error-text">{errors.lastName}</small>
          )}
        </div>

        <div className={`input-field ${errors.email ? "has-error" : ""}`}>
          <span className="fIcon">
            <FaEnvelope />
          </span>
          <input
            placeholder="Email"
            name="email"
            type="email"
            value={email}
            onInvalid={(e) => e.preventDefault()}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email &&
            (<small className="error-text">
              {errors.email}
            </small>)
          }
          {/* {isEmailChecking && (
            <small className="info-text">
              Sprawdzanie dostępności e-maila...
            </small>
          )} */}
        </div>
        <div className={`input-field password-field${errors.password ? "has-error" : ""}`}>
          <span className="fIcon">
            <FaLock />
          </span>
          <input
            placeholder="Hasło"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="password-toggle" onClick={() => setShowPassword((prev) => !prev)}>
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
          {errors.password && (
            <small className="error-text">{errors.password}</small>
          )}
        </div>

        <div className={`input-field ${errors.phoneNumber ? "has-error" : ""}`}>
          <span className="fIcon">
            <FaPhoneAlt />
          </span>
          <input
            placeholder="Numer telefonu (opcjonalnie)"
            name="phonenumber"
            type="phonenumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          {errors.phoneNumber && (
            <small className="error-text">{errors.phoneNumber}</small>
          )}
        </div>

        <button type="submit" className="btn btn-primary btn-block mt-2 iBtn">
          Utwórz konto
        </button>

        {/* <p className="social-text">Zarejestruj się z użyciem konta Google</p> */}
        {/* <SocialSign /> */}
      </form>

      <PopUpVerificationCode
        email={registeredEmail}
        visible={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerified={() => {
          setShowVerificationModal(false);
          setSignUp(false);
        }}
      />
    </>
  );
};

export default SignUp;
